// Hotfix Z.20 — Auto-poll for platform releases.
//
// Operators repeatedly complained that they had to remember to open the
// Platform → System page and click "Check for updates" whenever we
// shipped a hotfix. This hook lifts that responsibility off them: while
// an admin session is open, the browser silently polls the configured
// manifest URL once an hour and surfaces a small "Update available" pill
// in the header + a single toast the first time a new version appears.
//
// Design decisions:
//   • Poll cadence is 60 minutes. Fast enough that operators find out
//     the same day, slow enough that the manifest storage bucket sees a
//     trivial request rate even with many admins logged in.
//   • First poll fires 8 s after mount (not immediately) to keep initial
//     shell paint snappy on slow VPS.
//   • "Already-notified-about-this-version" state persists in
//     localStorage under `elswitch:auto-update:notified:<stamp>` so
//     re-logins do not re-toast the same version.
//   • Dispatches a DOM `CustomEvent("elswitch:auto-update")` on every
//     check completion (with `{ latest, current, updateAvailable }`) so
//     the header pill and any other listeners can render consistently
//     without React context plumbing.
//   • Listens for `elswitch:version-changed` (fired by PlatformSystem
//     after a successful in-app upgrade) and re-checks immediately —
//     drops the pill within seconds of the upgrade completing.
//   • Silent on errors: a mis-configured manifest URL, an offline
//     bucket, or a 500 on the backend all just no-op. No error toasts
//     from the poll loop — the manual "Check for updates" button is
//     still the diagnostic path.

import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 60 * 60 * 1000; // 60 min
const FIRST_CHECK_DELAY_MS = 8 * 1000;   // 8 s after mount
const LS_KEY_PREFIX = "elswitch:auto-update:notified:";
const AUTO_UPDATE_EVENT = "elswitch:auto-update";
const VERSION_CHANGED_EVENT = "elswitch:version-changed";

function alreadyNotified(stamp) {
  if (!stamp) return false;
  try {
    return !!localStorage.getItem(LS_KEY_PREFIX + stamp);
  } catch {
    return false;
  }
}
function markNotified(stamp) {
  if (!stamp) return;
  try {
    localStorage.setItem(LS_KEY_PREFIX + stamp, String(Date.now()));
    // Best-effort GC: keep at most 20 remembered stamps so localStorage
    // doesn't grow unbounded over a very long-lived admin session.
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_KEY_PREFIX)) keys.push(k);
    }
    if (keys.length > 20) {
      keys
        .map((k) => [k, Number(localStorage.getItem(k) || "0")])
        .sort((a, b) => a[1] - b[1])
        .slice(0, keys.length - 20)
        .forEach(([k]) => {
          try { localStorage.removeItem(k); } catch { /* ignore */ }
        });
    }
  } catch { /* localStorage disabled — accept the re-toast on next login */ }
}

/**
 * Auto-polls for a new platform release once an hour. Returns
 * `{ latest, updateAvailable }`. Silently no-ops if the API is
 * unreachable or the manifest URL is not configured.
 *
 * @param {Object} [opts]
 * @param {(payload: {latest: object, version: string}) => void} [opts.onFirstDetection]
 *        Optional callback fired ONCE per new version (deduped via
 *        localStorage). If omitted, a default sonner toast is shown.
 */
export function useAutoUpdateCheck(opts = {}) {
  const [state, setState] = useState({
    latest: null,
    updateAvailable: false,
    checkedAt: null,
  });
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    let cancelled = false;
    let intervalHandle = null;

    const doCheck = async () => {
      if (cancelled) return;
      try {
        const r = await api.get("/platform/system/check");
        if (cancelled || !r?.data) return;
        const latest = r.data.latest || null;
        const available = !!r.data.update_available;
        setState({
          latest,
          updateAvailable: available,
          checkedAt: Date.now(),
        });
        // Broadcast for anyone else in the tree (header pill, e.g.)
        try {
          window.dispatchEvent(
            new CustomEvent(AUTO_UPDATE_EVENT, {
              detail: {
                latest,
                updateAvailable: available,
                checkedAt: Date.now(),
              },
            }),
          );
        } catch { /* SSR / older browsers — ignore */ }

        // First-detection toast (once per version)
        if (available && latest?.version && !alreadyNotified(latest.stamp || latest.version)) {
          markNotified(latest.stamp || latest.version);
          if (typeof optsRef.current?.onFirstDetection === "function") {
            try {
              optsRef.current.onFirstDetection({ latest, version: latest.version });
            } catch { /* consumer swallowed */ }
          } else {
            // Default UX: subtle info toast with a link-style body.
            toast.info(`Update available — v${latest.version}`, {
              description: "Open Platform → System to review and install.",
              duration: 10000,
            });
          }
        }
      } catch {
        // Silent: manifest URL missing / bucket unreachable / API 500.
        // The manual "Check for updates" button in Platform → System
        // remains the diagnostic path — we intentionally do NOT toast
        // errors from a background poll.
      }
    };

    // First poll after a short delay to keep the initial shell paint
    // snappy on slow VPS.
    const firstTimer = setTimeout(doCheck, FIRST_CHECK_DELAY_MS);
    intervalHandle = setInterval(doCheck, POLL_INTERVAL_MS);

    // Re-check when the in-app upgrade fires the "version-changed" event
    // so the pill drops within seconds of the upgrade completing.
    const onVersionChanged = () => { doCheck(); };
    window.addEventListener(VERSION_CHANGED_EVENT, onVersionChanged);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      if (intervalHandle) clearInterval(intervalHandle);
      window.removeEventListener(VERSION_CHANGED_EVENT, onVersionChanged);
    };
  }, []);

  return state;
}

/** Constant strings exported so consumers stay in sync. */
export const AUTO_UPDATE_EVENTS = {
  UPDATE: AUTO_UPDATE_EVENT,
  VERSION_CHANGED: VERSION_CHANGED_EVENT,
};
